import { observable, action, computed } from "mobx";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import AwsS3Multipart from "@uppy/aws-s3-multipart";
import uuidv4 from "uuid/v4";
import MediaItem from "~/models/Media/MediaItem";
import MediaItemRepository from "~/repository/Media/MediaItemRepository";
import FormStore from "~/stores/FormStore";
import DnDStore from "~/stores/DnDStore";
import NestStore from "~/stores/NestStore";
import TourInboxImageRepository from "~/repository/TourInboxImageRepository";
import TourInboxImageUploadNotifyRepository from "~/repository/TourInboxImageUploadNotifyRepository";
import MediaItemSequenceRepository from "~/repository/Media/MediaItemSequenceRepository";
import MediaItemBulkUpdateRepository from "~/repository/Media/MediaItemBulkUpdateRepository";
import AwsS3UploadSignedUrlStore from "~/stores/AwsS3/AwsS3UploadSignedUrlStore";
import AwsS3MultipartUploadStore from "~/stores/AwsS3/AwsS3MultipartUploadStore";
import AwsS3UploadPartSignedUrlStore from "~/stores/AwsS3/AwsS3UploadPartSignedUrlStore";
import AwsS3ListPartStore from "~/stores/AwsS3/AwsS3ListPartStore";
import CommonHelper from "~/utils/CommonHelper";
import MediaItemClassName from "~/constants/Media/MediaItemClassName";

const CONTENT_TYPE_BY_EXTENSION = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
};

const MULTIPART_UPLOAD_THRESHOLD = 50 * 1024 * 1024;
const MULTIPART_CHUNK_SIZE = 10 * 1024 * 1024;
const UPLOAD_TIMEOUT = 2 * 60 * 1000;
const MULTIPART_RETRY_DELAYS = [0, 1000, 3000, 5000, 10000];

class MediaItemStore extends NestStore {
  tourId = null;

  /**
   * @property {Array} of Media model
   */
  @observable mediaItems = [];

  /**
   * @property {Array} of String with media meta id (Photo_1, etc).
   */
  @observable sequences = [];

  /**
   * @property {String} editingMediaItemId track which media item id is in "edit mode", in order to bind  formStore
   * to the MediaEditForm component
   */
  @observable editingMediaItemId = null;

  /**
   * @property {FormStore[]} inlineFormStores used for bulk editing media items names.
   */
  @observable inlineFormStores = {};

  @observable tourInbox = [];

  /**
   * @property {boolean} inboxRunning used for telling if the inbox is already being checked. Avoids running
   * concurrent requests.
   */
  inboxRunning = false;

  /**
   * @property {?number} timer id for the next scheduled inbox poll. Tracked so that re-entries
   * (e.g. a new upload-success during the 3s gap between polls) collapse onto a single chain
   * instead of spawning parallel ones.
   */
  inboxTimerId = null;

  inboxPulling = true;

  /**
   * @property {MediaItemRepository}
   */
  mediaItemRepository = null;

  /**
   * @property {TourInboxImageRepository}
   */
  tourInboxImageRepository = null;

  /**
   * @property {MediaItemSequenceRepository}
   */
  mediaItemSequenceRepository = null;

  /**
   * @property {MediaItemBulkUpdateRepository}
   */
  mediaItemBulkUpdateRepository = null;

  /**
   * @property {TourInboxImageUploadNotifyRepository}
   */
  tourInboxImageUploadNotifyRepository = null;

  /**
   * @property {AwsS3UploadSignedUrlStore}
   */
  awsS3UploadSignedUrlStore = null;

  /**
   * @property {AwsS3MultipartUploadStore}
   */
  awsS3MultipartUploadStore = null;

  /**
   * @property {AwsS3UploadPartSignedUrlStore}
   */
  awsS3UploadPartSignedUrlStore = null;

  /**
   * @property {AwsS3ListPartStore}
   */
  awsS3ListPartStore = null;

  /**
   * @property {Map<string, string>} S3 keys minted per Uppy file id, used by the notify call after PUT.
   */
  s3KeysByFileId = new Map();

  /**
   * @property {Uppy}
   */
  uploader = null;

  /**
   * @property {Uppy}
   */
  @observable multipartUploader = null;

  /**
   * @property {DnDStore}
   */
  @observable dndStore = null;

  constructor() {
    super();

    this.mediaItemRepository = new MediaItemRepository();
    this.tourInboxImageRepository = new TourInboxImageRepository();
    this.mediaItemSequenceRepository = new MediaItemSequenceRepository();
    this.mediaItemBulkUpdateRepository = new MediaItemBulkUpdateRepository();
    this.tourInboxImageUploadNotifyRepository = new TourInboxImageUploadNotifyRepository();
    this.awsS3UploadSignedUrlStore = new AwsS3UploadSignedUrlStore();
    this.awsS3MultipartUploadStore = new AwsS3MultipartUploadStore();
    this.awsS3UploadPartSignedUrlStore = new AwsS3UploadPartSignedUrlStore();
    this.awsS3ListPartStore = new AwsS3ListPartStore();
    this.dndStore = new DnDStore("mediaItemId");
    this.inboxPulling = true;

    // callback functions requires context binding in order to access the right "this" when invoked.
    this.bodyClickListener = this.bodyClickListener.bind(this);
  }

  /**
   * Return a formStore corresponding the media item that is in edit mode.
   * Note: cannot be turn into a computed property because the formStore can change without the need of
   * the editingMediaItemId to change as well.
   */
  getFormStore() {
    if (!this.editingMediaItemId) {
      // null object
      return new FormStore({});
    }

    if (this.inlineFormStores.length === 0) {
      // null object
      return new FormStore({});
    }

    if (typeof this.inlineFormStores[this.editingMediaItemId] === "undefined") {
      // null object
      return new FormStore({});
    }

    return this.inlineFormStores[this.editingMediaItemId];
  }

  @computed
  get currentSequences() {
    return this.mediaItems.map(mediaItem => mediaItem.mediaItemId);
  }

  @computed
  get hasSequencesChanged() {
    const original = this.sequences.join(",");
    const current = this.currentSequences.join(",");

    return original !== current;
  }

  @computed
  get inlineFormStoresDirty() {
    return Object.keys(this.inlineFormStores).some(key => {
      // This computed property is used for enabling/disabling the "save" button. Because of that we don't
      // want to enable the button if all inline forms have an empty name. Note that the button will be enabled as long
      // as there's a dirty but also valid form. Invalid forms will be ignored (see saveInlineFormStores())
      return this.inlineFormStores[key].isDirty && this.inlineFormStores[key].model.name.trim() !== "";
    });
  }

  @action
  load(tourId) {
    this.tourId = tourId;
    this.isLoading = true;
    // load media items. Return its promise so page can show a proper loading feedback.
    return this.loadMediaItems(tourId).finally(() => {
      this.isLoading = false;
    });
  }

  @action
  configureUploader(tourId) {
    // if uploader is already loaded then let's just reset it
    if (this.uploader) {
      this.uploader.reset();
      this.multipartUploader.reset();
      this.s3KeysByFileId.clear();

      return;
    }

    this.uploader = Uppy({
      allowMultipleUploads: true,
      autoProceed: true,
      restrictions: { allowedFileTypes: [".jpg", ".jpeg", ".mov", ".mp4", ".avi"] },
    });

    const { awsS3UploadSignedUrlStore, s3KeysByFileId } = this;

    this.uploader.use(AwsS3, {
      limit: 3,
      timeout: UPLOAD_TIMEOUT,
      getUploadParameters(file) {
        const extension = (file.extension || file.name.split(".").pop() || "").toLowerCase();
        const contentType = CONTENT_TYPE_BY_EXTENSION[extension] || file.type || "application/octet-stream";
        const key = `tourInboxImages/${tourId}/${uuidv4()}.${extension}`;
        s3KeysByFileId.set(file.id, key);

        return awsS3UploadSignedUrlStore.getOne({ implementation: "tourInboxImages", key, contentType }).then(data => ({
          method: "PUT",
          url: data.url,
          fields: null,
          headers: { "Content-Type": contentType },
        }));
      },
    });

    this.multipartUploader = Uppy({
      allowMultipleUploads: true,
      autoProceed: true,
      restrictions: { allowedFileTypes: [".jpg", ".jpeg", ".mov", ".mp4", ".avi"] },
    });

    const { awsS3MultipartUploadStore, awsS3UploadPartSignedUrlStore, awsS3ListPartStore } = this;
    const retryMultipartRequest = this.retryMultipartRequest.bind(this);

    this.multipartUploader.use(AwsS3Multipart, {
      limit: 2,
      timeout: UPLOAD_TIMEOUT,
      retryDelays: MULTIPART_RETRY_DELAYS,
      getChunkSize: () => MULTIPART_CHUNK_SIZE,
      createMultipartUpload(file) {
        const extension = (file.extension || file.name.split(".").pop() || "").toLowerCase();
        const contentType = CONTENT_TYPE_BY_EXTENSION[extension] || file.type || "application/octet-stream";
        const key = `tourInboxImages/${tourId}/${uuidv4()}.${extension}`;
        s3KeysByFileId.set(file.id, key);

        return retryMultipartRequest(() =>
          awsS3MultipartUploadStore.create({
            implementation: "tourInboxImages",
            key,
            contentType,
          }),
        );
      },
      listParts(file, { key, uploadId }) {
        return retryMultipartRequest(() =>
          awsS3ListPartStore.getAll({
            implementation: "tourInboxImages",
            key,
            uploadId,
          }),
        ).then(data => data.Parts || []);
      },
      prepareUploadPart(file, partData) {
        return retryMultipartRequest(() =>
          awsS3UploadPartSignedUrlStore.getOne({
            implementation: "tourInboxImages",
            partData: {
              key: partData.key,
              uploadId: partData.uploadId,
              number: String(partData.number),
            },
          }),
        );
      },
      completeMultipartUpload(file, { key, uploadId, parts }) {
        return retryMultipartRequest(() =>
          awsS3MultipartUploadStore.update({
            implementation: "tourInboxImages",
            key,
            uploadId,
            parts,
          }),
        );
      },
      abortMultipartUpload(file, { key, uploadId }) {
        return awsS3MultipartUploadStore.delete(uploadId, key);
      },
    });

    this.uploader.on("upload-success", file => {
      this.notifyTourInboxUpload(tourId, file);
    });

    this.multipartUploader.on("upload-success", file => {
      this.notifyTourInboxUpload(tourId, file);
    });

    this.configureUploaderCleanup(this.uploader);
    this.configureUploaderCleanup(this.multipartUploader);
  }

  notifyTourInboxUpload(tourId, file) {
    const s3Key = this.s3KeysByFileId.get(file.id);
    this.s3KeysByFileId.delete(file.id);
    if (!s3Key) return;

    this.tourInboxImageUploadNotifyRepository
      .notify({
        entityId: tourId,
        s3Key,
        originalFileName: file.name,
        autoProcess: true,
        slugifyFileName: true,
      })
      .then(() => this.checkTourInbox(tourId));
  }

  configureUploaderCleanup(uploader) {
    // Once all files are done, drop them from Uppy's state so the StatusBar counters reset for
    // the next upload. If files were added mid-batch they will still be in-flight when this fires,
    // so we wait for the subsequent complete event when everything is truly finished.
    uploader.on("complete", () => {
      const files = uploader.getFiles();
      if (files.some(f => f.progress.uploadStarted && !f.progress.uploadComplete)) {
        return;
      }
      files.forEach(f => {
        this.s3KeysByFileId.delete(f.id);
        uploader.removeFile(f.id);
      });
    });
  }

  retryMultipartRequest(request, attempt = 0) {
    return Promise.resolve()
      .then(request)
      .catch(error => {
        if (!this.isRetryableMultipartError(error) || attempt >= MULTIPART_RETRY_DELAYS.length) {
          throw error;
        }

        return new Promise(resolve => {
          setTimeout(resolve, MULTIPART_RETRY_DELAYS[attempt]);
        }).then(() => this.retryMultipartRequest(request, attempt + 1));
      });
  }

  isRetryableMultipartError(error) {
    const statusCode = error && (error.statusCode || error.status || (error.source && error.source.status));

    if (!statusCode) {
      return true;
    }

    return statusCode === 408 || statusCode === 409 || statusCode === 423 || statusCode === 429 || statusCode >= 500;
  }

  addFile(file) {
    if (file.size > MULTIPART_UPLOAD_THRESHOLD) {
      this.multipartUploader.addFile(file);
      return;
    }

    this.uploader.addFile(file);
  }

  @action
  checkTourInbox(tourId) {
    // Cancel any pending scheduled poll — we're polling now (or will be after the in-flight one
    // returns). Without this, multiple upload-success events landing during the 3s gap stack up
    // independent polling chains.
    if (this.inboxTimerId) {
      clearTimeout(this.inboxTimerId);
      this.inboxTimerId = null;
    }

    if (this.inboxRunning || !this.inboxPulling) {
      return;
    }

    this.inboxRunning = true;

    this.tourInboxImageRepository.fetch({ tourId }).then(response => {
      this.inboxRunning = false;

      // Let's calculate if we should reload the media items. We will do it if there are fewer items
      // in the inbox now than the last run.
      if (response.data.tourInboxImages.length < this.tourInbox.length) {
        this.loadMediaItems(tourId);
      }

      this.tourInbox = response.data.tourInboxImages;

      // If there are no tour inbox images, then break the recursive check.
      if (response.data.tourInboxImages.length === 0) {
        return;
      }

      // run recursively until inbox is equal to 0.
      // wait at least some seconds before the next run.
      this.inboxTimerId = setTimeout(() => this.checkTourInbox(tourId), 3000);
    });
  }

  @action
  loadMediaItems(tourId) {
    return this.mediaItemRepository.fetch({ tourId }).then(response => {
      this.mediaItems.clear();
      this.sequences.clear();
      const mediaItems = [];

      response.data.mediaItems.forEach(mediaItem => {
        /** @type MediaItem */
        const mediaInstance = new MediaItem(mediaItem);
        // Create a corresponding form that will be used for updating the media item name "inline" without
        // needing to open the full media item form.
        if (typeof this.inlineFormStores[mediaInstance.mediaItemId] === "undefined") {
          const form = this.createFormStore(mediaInstance);
          this.inlineFormStores[mediaInstance.mediaItemId] = form;
        }

        mediaItems.push(mediaInstance);
      });

      this.mediaItems = mediaItems;
      // Store the sequences. They will only be used to compute if items have been sorted since they were loaded.
      this.sequences = this.currentSequences;
    });
  }

  @action
  bindMediaItemToForm(mediaItemId) {
    const selectedMediaItem = this.mediaItems.find(m => m.mediaItemId === mediaItemId);

    if (!selectedMediaItem) {
      throw new Error(`Unable to find media item by id ${mediaItemId}`);
    }

    this.editingMediaItemId = selectedMediaItem.mediaItemId;
  }

  @action
  createFormStore(mediaItem) {
    const initialModel = {
      // This will bind mediaItem.mediaItemId, although it's not used in the form per-se, it will be handy to have
      // so we can later find the actual mediaItem to which it belongs to.
      ...mediaItem,
      mediaItemId: mediaItem.mediaItemId,
    };

    // VideoClip don't support changing duration since their duration cannot be extended by just changing a value.
    if (mediaItem.className === MediaItemClassName.VIDEO_CLIP) {
      delete initialModel.duration;
    }

    const formStore = new FormStore(initialModel);

    return formStore;
  }

  @action
  resetForm() {
    this.getFormStore().reset();
  }

  @action
  save() {
    this.isSaving = true;

    return Promise.all([this.saveForm(), this.saveSequence()])
      .then(() => {
        return this.saveInlineFormStores();
      })
      .then(() => {
        this.errorMessages = {};
      })
      .catch(e => {
        if (e.status === "fail") {
          this.errorMessages = e.message;
        } else {
          throw e;
        }
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  @action
  saveForm() {
    const formStore = this.getFormStore();

    // save form only if something changed.
    if (!formStore.isDirty) {
      return Promise.resolve();
    }

    // find the media to save that is bound to the form, note that model.mediaItemId maps to media.mediaItemId
    const { model } = formStore;
    const { mediaItemId } = model;
    const mediaItem = this.mediaItems.find(m => m.mediaItemId === mediaItemId);

    if (!mediaItem) {
      throw new Error(`Could not find media to save by id: ${mediaItemId}`);
    }

    // create a transient MediaItem just to bind the form and then get the right payload depending on the media item
    // className.
    const mediaItemToSave = new MediaItem(model);
    // exclude id and className from payload. Backend doesn't expect those properties.
    const payload = mediaItemToSave.getFormData(["id", "className"]);

    return this.mediaItemRepository
      .update(CommonHelper.firstLetterLower(mediaItem.className), mediaItem.id, payload)
      .then(response => {
        // completely replace item that we just saved to reflect any side effects on the server.
        const indexAt = this.mediaItems.indexOf(mediaItem);
        const updatedMedia = new MediaItem(response.data.mediaItem);
        this.mediaItems.splice(indexAt, 1, updatedMedia);
        this.inlineFormStores[updatedMedia.mediaItemId] = this.createFormStore(updatedMedia);
      });
  }

  @action
  saveSequence() {
    if (!this.hasSequencesChanged) {
      return Promise.resolve();
    }

    // The media items for the sequences is an array of objects with the className and the id only
    const mediaItems = [];

    // We go through the currentSequences, to get the className and id
    for (let i = 0; i < this.currentSequences.length; i += 1) {
      const selectedMediaItem = this.mediaItems.find(m => m.mediaItemId === this.currentSequences[i]);

      mediaItems.push({ className: selectedMediaItem.className, id: selectedMediaItem.id });
    }

    return this.mediaItemSequenceRepository.update(this.tourId, { mediaItems }).then(() => {
      this.sequences = this.currentSequences;
    });
  }

  @action
  saveInlineFormStores() {
    if (!this.inlineFormStoresDirty) {
      return Promise.resolve();
    }

    const mediaItemsToSave = [];

    Object.keys(this.inlineFormStores).forEach(mediaItemId => {
      const formStore = this.inlineFormStores[mediaItemId];
      const { isDirty, model } = formStore;

      // skip if form is pristine or if the name is empty.
      if (!isDirty || model.name.trim() === "") {
        return;
      }

      /** @type MediaItem */
      const mediaItem = this.mediaItems.find(m => m.mediaItemId === mediaItemId);
      mediaItemsToSave.push({ className: mediaItem.className, id: mediaItem.id, name: model.name });
    });

    if (mediaItemsToSave.length === 0) {
      return Promise.resolve();
    }

    return this.mediaItemBulkUpdateRepository
      .update(this.tourId, { mediaItems: mediaItemsToSave })
      .then(() => {
        mediaItemsToSave.forEach(savedMediaItem => {
          const mediaItem = this.mediaItems.find(
            m => m.id === savedMediaItem.id && m.className === savedMediaItem.className,
          );
          mediaItem.setIsEditing(false);
          mediaItem.setName(savedMediaItem.name);
          const form = this.createFormStore(mediaItem);
          this.inlineFormStores[mediaItem.mediaItemId] = form;
        });
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  @action
  saveSelectedMediaItemsIncludeIn(includeIn) {
    this.isSaving = true;

    const mediaItemsToSave = [];
    for (let i = 0; i < this.mediaItems.length; i += 1) {
      if (this.mediaItems[i].isSelected) {
        mediaItemsToSave.push({ className: this.mediaItems[i].className, id: this.mediaItems[i].id, includeIn });
      }
    }

    if (mediaItemsToSave.length === 0) {
      throw new Error(`Error. No media items selected.`);
    }

    return this.mediaItemBulkUpdateRepository
      .update(this.tourId, { mediaItems: mediaItemsToSave })
      .then(() => {
        this.mediaItems
          .filter(m => m.isSelected)
          .forEach(m => {
            // update the models
            m.setIncludeIn(includeIn);
            // update the forms too.

            // If the form is already dirty then just change the model
            if (this.inlineFormStores[m.mediaItemId].isDirty) {
              this.inlineFormStores[m.mediaItemId].setProperty("includeIn", includeIn);
            } else {
              // if the form is pristine then we need to create a new fresh form which will reflect the
              // new includeIn change, otherwise if we only change the model then the form will appear as dirty.
              this.inlineFormStores[m.mediaItemId] = this.createFormStore(m);
            }
          });
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  @action
  cancelEdit(mediaItemId) {
    const mediaItem = this.mediaItems.find(m => m.mediaItemId === mediaItemId);
    this.inlineFormStores[mediaItemId] = this.createFormStore(mediaItem);
    mediaItem.setIsEditing(false);
  }

  @action
  setSelected(mediaItemId, isSelected) {
    for (let i = 0; i < this.mediaItems.length; i += 1) {
      const mediaItem = this.mediaItems[i];

      if (mediaItem.mediaItemId === mediaItemId) {
        mediaItem.setIsSelected(isSelected);

        // hard exit function.
        return;
      }
    }

    throw new Error(`Could not find media item by id: ${mediaItemId}`);
  }

  // DnDStore stuff

  @action
  onClick(mediaItemId, mouseEvent) {
    // DnD Stuff
    this.mediaItems = this.dndStore.onClick(this.mediaItems, mediaItemId, mouseEvent);

    // MediaItem inline editing stuff

    /** @type MediaItem */
    const mediaItem = this.mediaItems.find(m => m.mediaItemId === mediaItemId);
    mediaItem.setIsEditing(true);

    // for the rest of inline forms, hide them if pristine
    // hide all inline forms that are pristine.
    Object.keys(this.inlineFormStores).forEach(key => {
      // skip the current clicked media item, we just set isEditing true above.
      if (key !== mediaItemId && !this.inlineFormStores[key].isDirty) {
        const mediaItemInLoop = this.mediaItems.find(m => m.mediaItemId === key);
        mediaItemInLoop.setIsEditing(false);
      }
    });
  }

  @action
  onItemDragBegin(id) {
    this.mediaItems = this.dndStore.onItemDragBegin(this.mediaItems, id);
  }

  @action
  onItemDragEnd(sourceItem, targetItem) {
    this.mediaItems = this.dndStore.onItemDragEnd(this.mediaItems, sourceItem, targetItem);
  }

  @action
  reorder(sourceId, targetId) {
    this.mediaItems = this.dndStore.reorder(this.mediaItems, sourceId, targetId);
  }

  @action
  unselectAllMediaItems() {
    this.mediaItems = this.dndStore.unselectAllItems(this.mediaItems);
  }

  @action
  bodyClickListener(event) {
    // DnD Stuff
    this.mediaItems = this.dndStore.bodyClickListener(event, this.mediaItems, [
      ".mediaItemsBulkAction",
      ".inline-form-container",
    ]);

    // MediaItem inline editing stuff

    // if the click happened outside a card, then let's do some handling. Otherwise the click happened on
    // a card in which case we'll add some code at the "onClick" method here at MediaItemStore.
    if (!event.target.closest(".card.draggable")) {
      // hide all inline forms that are pristine.
      Object.keys(this.inlineFormStores).forEach(mediaItemId => {
        if (!this.inlineFormStores[mediaItemId].isDirty) {
          const mediaItem = this.mediaItems.find(m => m.mediaItemId === mediaItemId);
          mediaItem.setIsEditing(false);
        }
      });
    }
  }

  attachEventListeners() {
    document.body.addEventListener("click", this.bodyClickListener);
  }

  detachEventListeners() {
    document.body.removeEventListener("click", this.bodyClickListener);
  }
}

export default MediaItemStore;
